import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { CountLine, StockCount, Warehouse } from '../types';

const STATUS_STYLE: Record<StockCount['status'], 'ok' | 'warn' | 'info' | 'default'> = {
  offen: 'warn',
  gezaehlt: 'info',
  gebucht: 'ok',
  verworfen: 'default',
};

export function InventoryPage() {
  const { can, owners } = useAuth();
  const mayCount = can('admin', 'schichtleiter', 'lagerist', 'disponent');
  const mayClose = can('admin', 'schichtleiter', 'disponent');

  const [counts, setCounts] = useState<StockCount[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ count: StockCount; lines: CountLine[] } | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [newCount, setNewCount] = useState({ warehouseId: '', ownerId: '', name: '' });
  const [showForm, setShowForm] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      setCounts(await api<StockCount[]>('/api/inventory/counts'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void loadCounts();
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
  }, [loadCounts]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    try {
      setDetail(await api<{ count: StockCount; lines: CountLine[] }>(`/api/inventory/counts/${id}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) void loadDetail(openId);
  }, [openId, loadDetail]);

  async function create() {
    if (!newCount.warehouseId || !newCount.name) {
      return setError('Lager und Bezeichnung werden gebraucht.');
    }
    setBusy(true);
    setError('');
    try {
      const created = await api<StockCount>('/api/inventory/counts', {
        method: 'POST',
        body: {
          warehouseId: newCount.warehouseId,
          ownerId: newCount.ownerId || null,
          name: newCount.name,
        },
      });
      setMessage(`Inventur "${created.name}" mit ${created.line_count} Positionen angelegt.`);
      setShowForm(false);
      setNewCount({ warehouseId: '', ownerId: '', name: '' });
      await loadCounts();
      setOpenId(created.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveLine(line: CountLine, value: string) {
    const qty = Number(value.replace(',', '.'));
    if (!Number.isFinite(qty) || qty < 0) return;
    try {
      await api(`/api/inventory/counts/${openId}/lines/${line.id}`, {
        method: 'PUT',
        body: { qty },
      });
      if (openId) await loadDetail(openId);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function close() {
    if (!openId) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ bookedLines: number }>(`/api/inventory/counts/${openId}/close`, {
        method: 'POST',
      });
      setMessage(
        result.bookedLines === 0
          ? 'Inventur abgeschlossen - keine Differenzen.'
          : `Inventur abgeschlossen, ${result.bookedLines} Differenzbuchung(en) erzeugt.`,
      );
      await loadCounts();
      await loadDetail(openId);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <span className="spacer" />
        {mayCount && (
          <button className="primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Abbrechen' : 'Neue Inventur'}
          </button>
        )}
      </div>

      {showForm && (
        <Card title="Neue Inventur">
          <p className="small muted" style={{ marginTop: 0 }}>
            Beim Anlegen wird der aktuelle Bestand als Zählvorlage eingefroren. Die Differenz wird
            beim Abschluss gegen den dann aktuellen Bestand gerechnet, nicht gegen die Vorlage.
          </p>
          <div className="form-grid">
            <label className="field">
              <span>Lager</span>
              <select
                value={newCount.warehouseId}
                onChange={(e) => setNewCount({ ...newCount, warehouseId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Eigentümer (optional)</span>
              <select
                value={newCount.ownerId}
                onChange={(e) => setNewCount({ ...newCount, ownerId: e.target.value })}
              >
                <option value="">alle Eigentümer</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Bezeichnung"
              value={newCount.name}
              onChange={(v) => setNewCount({ ...newCount, name: v })}
              hint="z. B. Stichtag 2026-12-31"
            />
          </div>
          <button className="primary" onClick={create} disabled={busy}>
            {busy ? 'Anlegen …' : 'Inventur anlegen'}
          </button>
        </Card>
      )}

      <Card title="Inventuren" padded={false}>
        {!counts ? (
          <Loading />
        ) : counts.length === 0 ? (
          <Empty>Noch keine Inventur angelegt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Bezeichnung</th>
                <th>Lager</th>
                <th>Eigentümer</th>
                <th>Status</th>
                <th className="num">Positionen</th>
                <th className="num">gezählt</th>
                <th className="num">Differenzen</th>
                <th>Angelegt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {counts.map((count) => (
                <tr key={count.id}>
                  <td>{count.name}</td>
                  <td className="mono small">{count.warehouse_code}</td>
                  <td className="small">{count.owner_name ?? 'alle'}</td>
                  <td>
                    <Badge kind={STATUS_STYLE[count.status]}>{count.status}</Badge>
                  </td>
                  <td className="num">{count.line_count}</td>
                  <td className="num">{count.counted_count}</td>
                  <td className="num" style={count.difference_count > 0 ? { color: 'var(--bad)' } : undefined}>
                    {count.difference_count}
                  </td>
                  <td className="small muted">{formatDateTime(count.created_at)}</td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenId(count.id)}>
                      öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && (
        <Card
          title={detail ? `Zählliste: ${detail.count.name}` : 'Zählliste'}
          actions={
            <>
              {detail && mayClose && detail.count.status !== 'gebucht' && (
                <button className="primary small" onClick={close} disabled={busy}>
                  Inventur abschliessen und buchen
                </button>
              )}
              <button className="small" onClick={() => setOpenId(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          {!detail ? (
            <Loading />
          ) : detail.lines.length === 0 ? (
            <Empty>Keine Positionen - im gewählten Lager liegt kein Bestand.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Platz</th>
                    <th>Artikel</th>
                    <th>Eigentümer</th>
                    <th>Charge</th>
                    <th className="num">Soll</th>
                    <th className="num">Gezählt</th>
                    <th className="num">Differenz</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line) => {
                    const difference =
                      line.qty_counted === null ? null : line.qty_counted - line.qty_expected;
                    return (
                      <tr key={line.id}>
                        <td className="mono">{line.location_code}</td>
                        <td>
                          <span className="mono">{line.sku}</span> {line.article_name}
                        </td>
                        <td className="small">{line.owner_name}</td>
                        <td className="small">{line.lot_code ?? '—'}</td>
                        <td className="num muted">{formatQty(line.qty_expected, line.unit)}</td>
                        <td className="num" style={{ width: 120 }}>
                          {detail.count.status === 'gebucht' ? (
                            line.qty_counted === null ? '—' : formatQty(line.qty_counted)
                          ) : (
                            <input
                              style={{ textAlign: 'right' }}
                              defaultValue={line.qty_counted ?? ''}
                              onBlur={(e) => void saveLine(line, e.target.value)}
                              disabled={!mayCount}
                            />
                          )}
                        </td>
                        <td
                          className="num"
                          style={difference ? { color: 'var(--bad)', fontWeight: 600 } : undefined}
                        >
                          {difference === null ? '—' : difference > 0 ? `+${difference}` : difference}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
